import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Info, X } from 'lucide-react';
import { TIER_DEFINITIONS, TIER_STYLES } from '../constants';
import usePopoverAnchor from './usePopoverAnchor';

/**
 * Anchored popover explaining the four JPRO tiers. Highlights whichever tier
 * the property currently lands in so the user can place their score in context.
 */
export default function TierLegendPopover({ currentTier, anchorRef, open, onClose }) {
    const popoverRef = useRef(null);
    const positionStyle = usePopoverAnchor(anchorRef, open, { preferredWidth: 384 });

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e) => {
            if (popoverRef.current && popoverRef.current.contains(e.target)) return;
            if (anchorRef?.current && anchorRef.current.contains(e.target)) return;
            onClose?.();
        };
        const handleEsc = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEsc);
        };
    }, [open, onClose, anchorRef]);

    if (!open) return null;

    return createPortal(
        <div ref={popoverRef}
             style={positionStyle}
             className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg ring-1 ring-black/5">
            <div className="flex items-start justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 dark:text-white tracking-tight">JPRO Index Tiers</div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Where your score sits — and what each band means</div>
                </div>
                <button onClick={onClose} className="p-0.5 -mr-1 -mt-0.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors">
                    <X size={14} />
                </button>
            </div>

            <ul className="p-4 space-y-3">
                {TIER_DEFINITIONS.map(def => {
                    const style = TIER_STYLES[def.tier];
                    const isCurrent = def.tier === currentTier;
                    return (
                        <li key={def.tier}
                            className={`p-2.5 rounded-lg border transition-colors
                                        ${isCurrent
                                            ? 'border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/40'
                                            : 'border-transparent'}`}>
                            <div className="flex items-center gap-2 mb-1">
                                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${style.bg} ${style.fg}`}>
                                    {def.tier}
                                </span>
                                <span className="text-[11px] tabular-nums text-slate-500 dark:text-slate-400">{def.range}</span>
                                {isCurrent && (
                                    <span className="text-[10px] uppercase tracking-wider font-semibold text-blue-600 dark:text-blue-400 ml-auto">
                                        You're here
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-slate-700 dark:text-slate-200 leading-relaxed">{def.meaning}</p>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed mt-1.5">{def.unlock}</p>
                        </li>
                    );
                })}
            </ul>
        </div>,
        document.body
    );
}

export function TierLegendButton({ onClick, buttonRef }) {
    return (
        <button ref={buttonRef} type="button" onClick={onClick}
                className="p-1 -m-1 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                title="What does this tier mean?">
            <Info size={14} />
        </button>
    );
}
